declare const ajaxUrl: string;

interface ArticlesData {
    posts: string[];
    has_more_posts: boolean;
    pages_count: number;
}

interface Pagination {
    container: HTMLElement;
    previousButton: HTMLElement;
    nextButton: HTMLElement;
    pages: HTMLElement;
}

interface Articles {
    loadArticles: () => void;
    section?: HTMLElement;
    container: HTMLElement;
    content: HTMLElement;
    pagination: Pagination;
    category?: string;
    currentPage: number;
    pagesCount: number;
    noPosts?: HTMLElement;
    categorySelect?: HTMLSelectElement;
}

type Blog = {
    newestArticles?: Articles,
    olderArticles?: Articles,
    paginationCenterThreshold: number;
    categories?: NodeListOf<HTMLButtonElement>;
};

(function() {
    const blog: Blog = {
        paginationCenterThreshold: 3,
    };
    
    const fetchArticles = async (searchText: string, categorySlug: string, page: number, position: 'vertical' | 'horizontal' = 'vertical', additionalOffset: number = 0): Promise<ArticlesData> => {
        const formData = new FormData();
        formData.append('action', 'get_articles');
        formData.append('search_text', searchText);
        formData.append('category', categorySlug);
        formData.append('page', page.toString());
        formData.append('position', position);
        formData.append('additional_offset', additionalOffset.toString());
    
        try {
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: formData,
            });
        
            if (!response.ok) {
                console.error('Error fetching articles:', response.statusText);
                return { posts: [], has_more_posts: false, pages_count: 0 };
            }
        
            const data = await response.json() as ArticlesData;
            return data;
        } catch(err) {
            console.error('Error fetching articles:', err);
        }
    
        return { posts: [], has_more_posts: false, pages_count: 0 };
    };
    
    const createPaginationHTML = (page: number, currentPage: number, type: 'newest' | 'older'): string => {
        return `
            <div class="pagination-page w-10 h-10 flex items-center justify-center rounded-full cursor-pointer ${page === currentPage ? (type === 'newest' ? 'bg-primary-300' : 'bg-primary-200') : ''}"
                data-page="${page}">
                <span class="text-muted-400">${page}</span>
            </div>
        `;
    }
    
    const createSkeletonVertical = (count: number): string => {
        let html = '';
        for(let i = 0; i < count; i++) {
            html += `
                <div class="skeleton-vertical w-[372px] h-[530px] flex flex-col gap-7 md:mx-0 mx-auto">
                    <div class="skeleton-vertical-item w-full h-full bg-muted-200 rounded-4xl animate-pulse"></div>
                </div>
            `;
        }
        return html;
    }
    
    const createSkeletonHorizontal = (count: number): string => {
        let html = '';
        for(let i = 0; i < count; i++) {
            html += `
                <div class="skeleton-horizontal w-full max-w-[372px] md:max-w-none h-[530px] md:h-[364px] flex flex-col gap-7 md:mx-0 mx-auto">
                    <div class="skeleton-horizontal-item w-full h-full bg-muted-200 rounded-4xl animate-pulse"></div>
                </div>
            `;
        }
        return html;
    }
    
    const loadPagination = (currentPage: number, pagesCount: number, pagination: Pagination, type: 'newest' | 'older') => {
        pagination.pages.innerHTML = '';
        // Determine range of visible pages
        let startPage = Math.max(1, currentPage - blog.paginationCenterThreshold);
        let endPage = Math.min(pagesCount, currentPage + blog.paginationCenterThreshold);
    
        if(pagesCount === 0) {
            pagination.pages.innerHTML += createPaginationHTML(1, 1, type);
        }
    
        // Always show first page if not in visible range
        if (startPage > 1) {
            pagination.pages.innerHTML += createPaginationHTML(1, currentPage, type);
            if (startPage > 2) {
                pagination.pages.innerHTML += createPaginationHTML(1, currentPage, type);
            }
        }
    
        for(let page = startPage; page <= endPage; page++) {
            pagination.pages.innerHTML += createPaginationHTML(page, currentPage, type);
        }
    
        // Always show last page if not in visible range
        if (endPage < pagesCount) {
            if (endPage < pagesCount - 1) {
                pagination.pages.innerHTML += `<div class="w-10 h-10 flex items-center justify-center rounded-full select-none pointer-events-none"><span class="text-muted-400">...</span></div>`;
            }
            pagination.pages.innerHTML += createPaginationHTML(pagesCount, currentPage, type);
        }
    }
    
    const loadNewestArticles = async () => {
        if(!blog.newestArticles) return;
    
        try {
            blog.newestArticles.content.innerHTML = createSkeletonVertical(6);
            blog.newestArticles.noPosts?.classList.add('hidden');
            blog.newestArticles.content.classList.remove('hidden');
            blog.newestArticles.pagination.container.classList.add('opacity-20', 'pointer-events-none');
    
            let category = blog.newestArticles.category || '';
            if(category === 'all') category = '';
    
            const data = await fetchArticles('', category, blog.newestArticles.currentPage, 'vertical');
            blog.newestArticles.content.innerHTML = data.posts.join('');
            blog.newestArticles.pagesCount = data.pages_count;
            loadPagination(blog.newestArticles.currentPage, data.pages_count, blog.newestArticles.pagination, 'newest');
            loadPaginationFuncionality(blog.newestArticles);
            blog.newestArticles.pagination.container.classList.remove('hidden');
            blog.newestArticles.pagination.container.classList.remove('opacity-20', 'pointer-events-none');
    
            if(data.posts.length === 0) {
                blog.newestArticles.noPosts?.classList.remove('hidden');
                blog.newestArticles.content.classList.add('hidden');
            }
        } catch(err) {
            console.error('Error loading newest articles:', err);
        }
    }
    
    const loadPaginationFuncionality = (articles: Articles) => {
        articles.pagination.previousButton.addEventListener('click', () => {
            if(articles.currentPage <= 1) return;
            articles.currentPage--;
            articles.loadArticles();
        });
        articles.pagination.nextButton.addEventListener('click', () => {
            if(articles.currentPage >= articles.pagesCount) return;
            articles.currentPage++;
            articles.loadArticles();
        });
        articles.pagination.pages.querySelectorAll('.pagination-page').forEach(page => {
            page.addEventListener('click', () => {
                if(page.classList.contains('pagination-page')) {
                    articles.currentPage = parseInt((page as HTMLElement).dataset.page || '1');
                    articles.loadArticles();
                }
            });
        });
    }
    
    const loadOlderArticles = async () => {
        if(!blog.olderArticles) return;
    
        try {
            blog.olderArticles.content.innerHTML = createSkeletonHorizontal(6);
    
            const data = await fetchArticles('', '', blog.olderArticles.currentPage, 'horizontal', 3);
    
            blog.olderArticles.content.innerHTML = data.posts.join('');
            loadPagination(blog.olderArticles.currentPage, data.pages_count, blog.olderArticles.pagination, 'older');
            loadPaginationFuncionality(blog.olderArticles);
            blog.olderArticles.pagesCount = data.pages_count;
            blog.olderArticles.pagination.container.classList.remove('hidden');
        } catch(err) {
            console.error('Error loading older articles:', err);
        }
    };
    
    const loadCategories = async () => {
        if(!blog.categories || !blog.newestArticles) return;
    
        for(const category of blog.categories) {
            category.addEventListener('click', async () => {
                const categorySlug = category.getAttribute('data-category');
                if(!categorySlug) return;
    
                blog.newestArticles!.category = categorySlug;
                blog.newestArticles!.currentPage = 1;
    
                if(blog.categories) {
                    blog.categories.forEach(category => {
                        category.classList.remove('filled');
                    });
                    category.classList.add('filled');
                }
    
                blog.olderArticles?.section?.classList.add('hidden');
    
                await loadNewestArticles();
                loadPaginationFuncionality(blog.newestArticles!);
            });
        }
    
        if(!blog.newestArticles?.categorySelect) return;
    
        blog.newestArticles.categorySelect.addEventListener('change', async () => {
            const categorySlug = blog.newestArticles!.categorySelect?.value;
            if(!categorySlug) return;
    
            blog.newestArticles!.category = categorySlug;
            blog.newestArticles!.currentPage = 1;
    
            blog.olderArticles?.section?.classList.add('hidden');
    
            await loadNewestArticles();
            loadPaginationFuncionality(blog.newestArticles!);
        });
    }
    
    window.addEventListener('load', () => {
        blog.newestArticles = {
            loadArticles: loadNewestArticles,
            container: document.getElementById('newest-articles') as HTMLElement,
            content: document.querySelector('#newest-articles .content') as HTMLElement,
            pagination: {
                container: document.querySelector('#newest-articles .pagination') as HTMLElement,
                previousButton: document.querySelector('#newest-articles .previous-button') as HTMLElement,
                nextButton: document.querySelector('#newest-articles .next-button') as HTMLElement,
                pages: document.querySelector('#newest-articles .pagination-pages') as HTMLElement,
            },
            category: '',
            currentPage: 1,
            pagesCount: 0,
            noPosts: document.querySelector('#newest-articles .no-posts') as HTMLElement,
            categorySelect: document.querySelector('.category-select') as HTMLSelectElement,
        };
        blog.olderArticles = {
            loadArticles: loadOlderArticles,
            section: document.getElementById('older-articles-section') as HTMLElement,
            container: document.getElementById('older-articles') as HTMLElement,
            content: document.querySelector('#older-articles .content') as HTMLElement,
            pagination: {
                container: document.querySelector('#older-articles .pagination') as HTMLElement,
                previousButton: document.querySelector('#older-articles .previous-button') as HTMLElement,
                nextButton: document.querySelector('#older-articles .next-button') as HTMLElement,
                pages: document.querySelector('#older-articles .pagination-pages') as HTMLElement,
            },
            currentPage: 1,
            pagesCount: 0,
        };
        blog.categories = document.querySelectorAll('.category-button');
    
        loadOlderArticles();
        loadCategories();
    });
})()